import { useMemo } from 'react';
import {
  keepPreviousData,
  skipToken,
  useQuery,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import type { CommonAggregation } from '../../types/domain';
import {
  dataApiClient,
  type AttributeAggregationType,
} from '../../infrastructure/dataApi/client';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { useMetaData } from './useMetaData';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';

type UseAttributeAggregationData = {
  isLoading: boolean;
  aggregations: Map<string, number>;
};

const EMPTY_DATA = new Map<string, never>();

const useAttributeAggregationData = (
  type: AttributeAggregationType
): UseAttributeAggregationData => {
  const { currentSimDataId } = useCurrentSimDataId();
  const { currentDataMeta } = useMetaData();
  const { apiFilter } = useApiDataFilter();

  // 棒グラフにはフィルターで除外せずに全要素を表示したいため条件から除外する
  const filter = { ...apiFilter, [type]: undefined };
  const viewMagnification = 1 / Number(currentDataMeta?.reduction_factor ?? 1);

  const { data, isFetching } = useQuery({
    queryKey: ['attributeAggregation', currentSimDataId, filter, type],
    queryFn: currentSimDataId
      ? async ({
          signal,
        }: QueryFunctionContext): Promise<CommonAggregation[]> => {
          return dataApiClient.getAttrAggr(
            currentSimDataId,
            filter,
            type,
            signal
          );
        }
      : skipToken,
    placeholderData: keepPreviousData,
    meta: { dataType: 'others' },
  });

  const aggregations = useMemo((): Map<string, number> | undefined => {
    return data
      ? new Map(
          data.map((item: CommonAggregation): [string, number] => {
            return [item.label, item.value * viewMagnification];
          })
        )
      : undefined;
  }, [data, viewMagnification]);

  return { isLoading: isFetching, aggregations: aggregations ?? EMPTY_DATA };
};

export { useAttributeAggregationData };
